import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import {
  FaPlane, FaChevronDown, FaShieldAlt, FaCheckCircle,
  FaHandshake, FaBus, FaLock, FaHeadset,
  FaStar, FaBolt, FaUsers,
  FaParking, FaAward,
} from "react-icons/fa";
import banner from "../../../common/images/banner.png";
import dateIcon from "../../../common/images/date.webp";
import timeIcon from "../../../common/images/time.webp";
import promoIcon from "../../../common/images/promo-code.webp";
import airportIcon from "../../../common/images/select-airport.png";
import { nextPage } from "../../../common/config/nextPage";
import initializeDepartureDate from "../../../common/libs/initializeDepartureDate";
import initializeArrivalDate from "../../../common/libs/initializeArrivalDate";
import cookieHandler from "../../../common/libs/cookieHandler";
import times from "../../../common/libs/times";
import { getStoredPromoCode } from "../../../common/libs/tracking";

/* ── Left bullets ── */
const LEFT_BULLETS = [
  "Official & off-airport parking options",
  "Terminals 2, 3, 4 & 5 all covered",
  "No hidden charges — transparent pricing",
  "Flexible cancellation on most bookings",
  "Instant email confirmation after booking",
];

/* ── Right stats cards ── */
const RIGHT_STATS = [
  { icon: <FaStar className="text-2xl" />, num: "4.9★", label: "Average Rating", sub: "From verified customers", bg: "bg-secondary", color: "text-primary" },
  { icon: <FaUsers className="text-2xl" />, num: "10,000+", label: "Happy Travellers", sub: "Book with us every year", bg: "bg-lightprimary", color: "text-white" },
  { icon: <FaAward className="text-2xl" />, num: "60%", label: "Average Saving", sub: "vs on-the-day airport rates", bg: "bg-secondary", color: "text-primary" },
  { icon: <FaBolt className="text-2xl" />, num: "5 min", label: "Quick Booking", sub: "Instant confirmation sent", bg: "bg-lightprimary", color: "text-white" },
];

const SeopageSearch = ({
  airports = [],
  airportName = "Heathrow",
  heroTitle,
  description1,
  backgroundImage = banner,
}) => {
  const [params] = useSearchParams();

  const getDeparture  = params.get("departure");
  const getArrival    = params.get("arrival");
  const getPromocode  = params.get("promocode");
  const getAirport    = params.get("airport");
  const singleProduct = params.get("product");
  const getCustomCid  = params.get("cid");

  const [selectOpen, setSelectOpen]       = useState(false);
  const [searchTerm, setSearchTerm]       = useState("");
  const [validation, setValidation]       = useState("");
  const [promocode, setPromoCode]         = useState(getPromocode || getStoredPromoCode() || "");
  const [airport, setAirport]             = useState(getAirport || "");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [departure, setDepartureState]    = useState(initializeDepartureDate(getDeparture || ""));
  const [arrival, setArrivalState]        = useState(initializeArrivalDate(getArrival || ""));

  const depPickerRef = useRef(null);
  const arrPickerRef = useRef(null);
  const dropdownRef  = useRef(null);

  // Outside click se airport dropdown close karo
  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSelectOpen(false);
        setSearchTerm("");
      }
    };
    if (selectOpen) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [selectOpen]);

  React.useEffect(() => { cookieHandler(getCustomCid); }, [getCustomCid]);

  const airportOptions = airports.map((a) => ({ label: a?.name, value: a?.code }));

  React.useEffect(() => {
    if (airport && airportOptions.length) {
      const found = airportOptions.find((a) => a.value === airport);
      if (found) setSelectedLabel(found.label);
    }
  }, [airport, airports]);

  const filtered = airportOptions.filter((a) =>
    a.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAirportSelect = (a) => {
    setAirport(a.value); setSelectedLabel(a.label);
    setValidation(""); setSelectOpen(false); setSearchTerm("");
  };

  const setDepDate = (date) => {
    const depTime = moment(departure).format("HH:mm");
    setDepartureState(moment(date).format("YYYY-MM-DD") + " " + depTime);
    const arrTime = moment(arrival).format("HH:mm");
    setArrivalState(moment(date).add(7, "days").format("YYYY-MM-DD") + " " + arrTime);
  };

  const setArrDate = (date) => setArrivalState(moment(date).format("YYYY-MM-DD HH:mm"));

  const setTime = (time, mode) => {
    const [h, m] = time.split(":").map(Number);
    if (mode === "arrival") setArrivalState(moment(arrival).set({ hours: h, minutes: m }).format("YYYY-MM-DD HH:mm"));
    else setDepartureState(moment(departure).set({ hours: h, minutes: m }).format("YYYY-MM-DD HH:mm"));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!airport) { setValidation("Please select an airport"); return; }
    const sp = new URLSearchParams({ departure, arrival, promocode, airport, product: singleProduct || "" });
    nextPage(`/search?${sp}`);
  };

  const fieldCls = "w-full pl-10 pr-3 py-3 bg-[#F2F4F7] border-0 rounded-xl text-sm font-bold text-primary outline-none cursor-pointer transition-all placeholder:text-primary/30";
  void fieldCls; // used via spread in some fields

  return (
    <>
      <link rel="preload" as="image" href={backgroundImage} fetchpriority="high" />

      <div className="relative w-full min-h-screen bg-primary overflow-x-hidden">

        {/* BG */}
        <img src={backgroundImage} alt={`${airportName} Airport Parking`} fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover object-center" style={{ zIndex: 0 }} />
        <div className="absolute inset-0 bg-primary/82" style={{ zIndex: 1 }} />

        {/* Blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-secondary/8 blur-3xl pointer-events-none" style={{ zIndex: 1 }} />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-lightprimary/10 blur-3xl pointer-events-none" style={{ zIndex: 1 }} />

        {/* ── CONTENT ── */}
        <div className="relative w-full max-w-[1400px] mx-auto px-4 sm:px-6"
          style={{ zIndex: 2, paddingTop: "110px", paddingBottom: "60px", minHeight: "100vh" }}>

          {/* Badge + heading */}
          <div className="text-center mb-10 md:mt-16 mt-0">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 rounded-full bg-lightprimary">
              <FaPlane className="text-white text-xs -rotate-45" />
              <span className="text-xs font-black text-white uppercase tracking-widest">Smart Deals, Happy Travels</span>
            </div>
            <h1 className="text-2xl font-bold text-white sm:text-3xl lg:text-4xl xl:text-5xl leading-tight">
              {heroTitle
                ? <span dangerouslySetInnerHTML={{ __html: heroTitle }} />
                : `Stress-Free ${airportName} Airport Parking`}
            </h1>
        
          </div>

          {/* ── 3-COL ── */}
          <div className="flex flex-col xl:flex-row items-start gap-8 xl:gap-6">

            {/* ══ LEFT — content ══ */}
            <div className="hidden xl:flex xl:w-[26%] flex-col gap-5 pt-2">
              <div>
                <h2 className="text-xl font-black text-white leading-snug mb-2">
                  Why Book <span className="text-secondary">With Us?</span>
                </h2>
                <p className="text-white text-sm leading-relaxed">
                  The UK's trusted airport parking comparison site — saving travellers time and money since day one.
                </p>
              </div>

              <div className="flex flex-col gap-2.5">
                {LEFT_BULLETS.map((pt, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-secondary flex items-center justify-center mt-0.5 shadow">
                      <FaCheckCircle className="text-primary text-[9px]" />
                    </span>
                    <p className="text-white text-sm leading-snug">{pt}</p>
                  </div>
                ))}
              </div>

              {/* Trust badge */}
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/10 border border-white/15 mt-2">
                <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0 shadow-lg">
                  <FaShieldAlt className="text-primary text-lg" />
                </div>
                <div>
                  <p className="text-white font-black text-sm">100% Safe & Secure</p>
                  <p className="text-white text-xs mt-0.5">All bookings fully protected</p>
                </div>
              </div>

              {/* Parking types */}
              <div className="flex flex-col gap-2">
                {[
                  { icon: <FaHandshake />, label: "Meet & Greet", bg: "bg-lightprimary" },
                  { icon: <FaBus />, label: "Park & Ride", bg: "bg-secondary" },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/8 border border-white/10 hover:bg-white/15 transition-colors">
                    <span className={`w-7 h-7 rounded-lg ${t.bg} flex items-center justify-center text-xs ${t.bg === "bg-secondary" ? "text-primary" : "text-white"} flex-shrink-0`}>
                      {t.icon}
                    </span>
                    <span className="text-white text-sm font-semibold">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ══ CENTER — form ══ */}
            <div className="w-full xl:w-[48%] flex justify-center">
              <div className="relative w-full max-w-[500px]">

                {/* Outer glow */}
                <div className="absolute -inset-3 bg-secondary/20 blur-3xl pointer-events-none" />

                {/* Outer decorative frame */}
                <div className="absolute -inset-[6px] border-2 border-secondary/40 rounded-tl-[2.5rem] rounded-br-[2.5rem] rounded-tr-lg rounded-bl-lg pointer-events-none" />

                {/* Form card — asymmetric corners: big top-left & bottom-right, small top-right & bottom-left */}
                <div className="relative overflow-hidden shadow-2xl rounded-tl-[2.5rem] rounded-br-[2.5rem] rounded-tr-lg rounded-bl-lg border border-white/10">

                  {/* ── Header strip ── */}
                  <div className="bg-primary px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-tl-2xl rounded-br-2xl rounded-tr-md rounded-bl-md bg-lightprimary flex items-center justify-center shadow">
                        <FaPlane className="text-white text-base -rotate-45" />
                      </div>
                      <div>
                        <p className="text-white font-black text-sm uppercase tracking-wide">Book Parking</p>
                        <p className="text-white text-xs">{airportName} Airport</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-secondary/20 px-3 py-1.5 rounded-tl-xl rounded-br-xl rounded-tr-sm rounded-bl-sm border border-secondary/30">
                      <FaShieldAlt className="text-secondary text-xs" />
                      <span className="text-secondary text-xs font-bold">Secure</span>
                    </div>
                  </div>

                  {/* ── Form body ── */}
                  <div className="bg-white px-6 py-5 sm:py-6">
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3">

                      {/* Airport */}
                      <div className="relative" ref={dropdownRef}>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-primary/40 mb-1">
                          Select Airport
                        </label>
                        <div onClick={() => setSelectOpen(!selectOpen)}
                          className="w-full px-4 py-3 bg-[#F2F4F7] rounded-tl-xl rounded-br-xl rounded-tr-md rounded-bl-md flex items-center justify-between cursor-pointer hover:bg-primary/5 transition-colors">
                          <div className="flex items-center gap-3">
                            <img src={airportIcon} alt="Airport" className="w-6 h-6 object-contain flex-shrink-0" />
                            <span className="text-sm font-bold text-primary truncate">
                              {selectedLabel || "Choose your airport..."}
                            </span>
                          </div>
                          <FaChevronDown className={`text-xs text-primary/40 flex-shrink-0 transition-transform duration-300 ${selectOpen ? "rotate-180" : ""}`} />
                        </div>
                        {selectOpen && (
                          <div className="absolute top-full left-0 w-full mt-1 bg-white border border-primary/10 rounded-xl shadow-2xl z-[9999] overflow-hidden">
                            <div className="p-2 border-b border-primary/10 flex items-center gap-2 bg-primary/5">
                              <FiSearch className="text-primary/40 text-sm ml-1 flex-shrink-0" />
                              <input autoFocus type="text" placeholder="Search airport..."
                                className="w-full py-1 text-sm bg-transparent outline-none text-primary font-semibold placeholder:text-primary/30"
                                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="max-h-44 overflow-y-auto">
                              {filtered.length > 0 ? filtered.map((a, i) => (
                                <div key={i} onClick={() => handleAirportSelect(a)}
                                  className="px-4 py-2.5 text-sm font-semibold text-primary hover:bg-secondary cursor-pointer border-b border-primary/5 last:border-none transition-colors">
                                  {a.label}
                                </div>
                              )) : <div className="p-4 text-center text-xs text-primary/40">No airports found</div>}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Drop-off row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-primary/40 mb-1">Drop-off</label>
                          <div className="flex items-center gap-2 bg-[#F2F4F7] rounded-tl-xl rounded-br-xl rounded-tr-md rounded-bl-md px-3 py-3 cursor-pointer hover:bg-primary/5 transition-colors"
                            onClick={() => { if (!depPickerRef.current?.isCalendarOpen()) depPickerRef.current?.setOpen(true); }}>
                            <img src={dateIcon} alt="Date" className="w-6 h-6 object-contain flex-shrink-0" />
                            <DatePicker
                              ref={depPickerRef}
                              selected={moment(departure).toDate()}
                              onChange={(date) => { setDepDate(date); depPickerRef.current?.setOpen(false); }}
                              minDate={moment().toDate()}
                              dateFormat="dd MMM yy"
                              popperPlacement="bottom-start"
                              className="text-sm font-bold text-primary bg-transparent outline-none cursor-pointer w-full"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-primary/40 mb-1">Time</label>
                          <div className="flex items-center gap-2 bg-[#F2F4F7] rounded-tl-xl rounded-br-xl rounded-tr-md rounded-bl-md px-3 py-3">
                            <img src={timeIcon} alt="Time" className="w-6 h-6 object-contain flex-shrink-0 pointer-events-none" />
                            <select className="text-sm font-bold text-primary bg-transparent outline-none cursor-pointer w-full appearance-none"
                              value={moment(departure).format("HH:mm")}
                              onChange={(e) => setTime(e.target.value, "departure")}>
                              {times.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Pick-up row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-primary/40 mb-1">Pick-up</label>
                          <div className="flex items-center gap-2 bg-[#F2F4F7] rounded-tl-xl rounded-br-xl rounded-tr-md rounded-bl-md px-3 py-3 cursor-pointer hover:bg-primary/5 transition-colors"
                            onClick={() => { if (!arrPickerRef.current?.isCalendarOpen()) arrPickerRef.current?.setOpen(true); }}>
                            <img src={dateIcon} alt="Date" className="w-6 h-6 object-contain flex-shrink-0" />
                            <DatePicker
                              ref={arrPickerRef}
                              selected={moment(arrival).toDate()}
                              onChange={(date) => { setArrDate(date); arrPickerRef.current?.setOpen(false); }}
                              minDate={moment(departure).toDate()}
                              dateFormat="dd MMM yy"
                              popperPlacement="bottom-start"
                              className="text-sm font-bold text-primary bg-transparent outline-none cursor-pointer w-full"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-primary/40 mb-1">Time</label>
                          <div className="flex items-center gap-2 bg-[#F2F4F7] rounded-tl-xl rounded-br-xl rounded-tr-md rounded-bl-md px-3 py-3">
                            <img src={timeIcon} alt="Time" className="w-6 h-6 object-contain flex-shrink-0 pointer-events-none" />
                            <select className="text-sm font-bold text-primary bg-transparent outline-none cursor-pointer w-full appearance-none"
                              value={moment(arrival).format("HH:mm")}
                              onChange={(e) => setTime(e.target.value, "arrival")}>
                              {times.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Promo */}
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-primary/40 mb-1">Promo Code</label>
                        <div className="flex items-center gap-2 bg-[#F2F4F7] rounded-tl-xl rounded-br-xl rounded-tr-md rounded-bl-md px-3 py-3">
                          <img src={promoIcon} alt="Promo" className="w-6 h-6 object-contain flex-shrink-0 pointer-events-none" />
                          <input type="text" placeholder="Enter promo code (optional)"
                            className="text-sm font-bold text-primary bg-transparent outline-none w-full placeholder:text-primary/30"
                            value={promocode} onChange={(e) => setPromoCode(e.target.value)} />
                        </div>
                      </div>

                      {validation && (
                        <p className="text-xs font-bold text-red-500 bg-red-50 border border-red-200 px-3 py-2 rounded-xl text-center">{validation}</p>
                      )}

                      {/* Submit */}
                      <button type="submit"
                        className="w-full py-4 bg-secondary hover:bg-primary text-primary hover:text-secondary border-2 border-secondary font-black uppercase text-sm tracking-widest rounded-tl-2xl rounded-br-2xl rounded-tr-md rounded-bl-md shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-2 mt-1">
                        <FiSearch className="text-base stroke-[3]" />
                        Get A Quote
                      </button>

                      {/* Trust row */}
                      <div className="flex items-center justify-center gap-4 pt-1 flex-wrap">
                        {[
                          { icon: <FaLock />, text: "Secure Payment" },
                          { icon: <FaCheckCircle />, text: "No Hidden Fees" },
                          { icon: <FaShieldAlt />, text: "Free Cancel*" },
                        ].map((t, i) => (
                          <div key={i} className="flex items-center gap-1.5 text-xs text-primary/40 font-semibold">
                            <span className="text-lightprimary">{t.icon}</span>{t.text}
                          </div>
                        ))}
                      </div>

                    </form>
                  </div>

                  {/* ── Bottom accent bar ── */}
                  <div className="h-2 bg-gradient-to-r from-secondary via-lightprimary to-secondary" />
                </div>
              </div>
            </div>

            {/* ══ RIGHT — stats cards ══ */}
            <div className="hidden xl:flex xl:w-[26%] flex-col gap-4 pt-2">

              <div className="mb-1">
                <h2 className="text-xl font-black text-white leading-snug mb-2">
                  Heathrow Parking <span className="text-secondary">By Numbers</span>
                </h2>
                <p className="text-white text-sm leading-relaxed">
                  Thousands of travellers trust us every year for the best Heathrow parking deals.
                </p>
              </div>

              {/* Stat cards — 2x2 grid */}
              <div className="grid grid-cols-2 gap-3">
                {RIGHT_STATS.map((s, i) => (
                  <div key={i}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl ${s.bg} shadow-lg hover:scale-105 transition-transform duration-300`}
                    style={{ borderRadius: i % 2 === 0 ? "1.5rem 0.5rem 1.5rem 0.5rem" : "0.5rem 1.5rem 0.5rem 1.5rem" }}
                  >
                    <span className={`mb-2 ${s.color}`}>{s.icon}</span>
                    <p className={`text-2xl font-black ${s.color} leading-none`}>{s.num}</p>
                    <p className={`text-xs font-bold ${s.color} mt-1 text-center leading-tight`}>{s.label}</p>
                    <p className={`text-xs ${s.color} opacity-70 mt-0.5 text-center leading-tight`}>{s.sub}</p>
                  </div>
                ))}
              </div>

              {/* Bottom CTA strip */}
         

            </div>

          </div>

          {/* Mobile strip */}
          <div className="xl:hidden mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {RIGHT_STATS.map((s, i) => (
              <div key={i} className={`flex items-center gap-3 ${s.bg} rounded-xl px-3 py-3 shadow-md`}>
                <span className={`text-xl flex-shrink-0 ${s.color}`}>{s.icon}</span>
                <div>
                  <p className={`font-black text-sm leading-tight ${s.color}`}>{s.num}</p>
                  <p className={`text-xs ${s.color} opacity-80`}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </>
  );
};

export default SeopageSearch;
